import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  Typography,
  Alert,
} from '@mui/material';
import { Cancel, CheckCircle } from '@mui/icons-material';
import CpdActionButton from '@/components/ui/CpdActionButton';
import CpdTextField from '@/components/ui/CpdTextField';
import CpdDropdown from '@/components/ui/CpdDropdown';
import useFormValidation from '@/hooks/useFormValidation';
import FormErrorSummary from '@/components/FormErrorSummary/FormErrorSummary';
import { formatBankAccountDisplay } from '@/utils/bank-account.util';

const CancelPaymentDialog = ({
  open,
  onClose,
  onConfirm,
  paymentId,
  payment,
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const {
    fieldErrors,
    generalErrors,
    setBackendErrors,
    clearAllErrors,
  } = useFormValidation();

  const hasBankAccount = !!payment?.client_bank_account_id;

  useEffect(() => {
    if (!open) {
      setComment('');
      setSubmitting(false);
      clearAllErrors();
    }
  }, [open, clearAllErrors]);

  const handleConfirm = async () => {
    clearAllErrors();

    if (!comment.trim()) {
      setBackendErrors({
        errors: [
          {
            msg: t('payments.cancelPaymentDialog.commentRequired'),
            path: 'comment',
          },
        ],
      });
      return;
    }

    if (!hasBankAccount) return;

    setSubmitting(true);
    try {
      await onConfirm(comment);
    } catch (err) {
      if (err.response?.data) {
        setBackendErrors(err.response.data);
      }
      throw err;
    } finally {
      setSubmitting(false);
    }
  };

  const getCustomerName = () => {
    if (!payment) return 'N/A';

    if (payment.business_payee_name) {
      return payment.business_payee_name;
    }

    const firstName = payment.first_name || '';
    const lastName = payment.last_name || '';
    return firstName || lastName ? `${firstName} ${lastName}`.trim() : 'N/A';
  };

  const getFormattedAmount = () => {
    if (!payment || !payment.refund_amount) return 'N/A';
    return `$${parseFloat(payment.refund_amount).toFixed(2)}`;
  };

  const isConfirmDisabled = !hasBankAccount || !comment.trim() || submitting;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: { minHeight: '400px' },
      }}>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Cancel color='primary' />
          {t('payments.cancelPaymentDialog.title')}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <FormErrorSummary errors={generalErrors} title='' />

          <Box>
            <Typography variant='h6' gutterBottom sx={{ fontSize: '14px' }}>
              {t('payments.cancelPaymentDialog.paymentDetails')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant='body2' color='text.secondary'>
                {t('common.labels.paymentId')}: <strong>{paymentId}</strong>
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {t('payments.table.customerInfo')}:{' '}
                <strong>{getCustomerName()}</strong>
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {t('payments.table.amount')}:{' '}
                <strong>{getFormattedAmount()}</strong>
              </Typography>
            </Box>
          </Box>

          <Box>
            <Typography variant='h6' gutterBottom sx={{ fontSize: '14px' }}>
              {t('payments.cancelPaymentDialog.moovAccountInfo')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography variant='body2' color='text.secondary'>
                {t('payments.cancelPaymentDialog.accountName')}:{' '}
                <strong>
                  {payment?.moov_account_name ||
                    t('payments.cancelPaymentDialog.notAvailable')}
                </strong>
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                {t('payments.cancelPaymentDialog.holderName')}:{' '}
                <strong>
                  {payment?.moov_holder_name ||
                    t('payments.cancelPaymentDialog.notAvailable')}
                </strong>
              </Typography>
            </Box>
          </Box>

          <Alert severity='warning' sx={{ mb: 1 }}>
            <Typography variant='body2'>
              {t('payments.cancelPaymentDialog.confirmMessage')}
            </Typography>
          </Alert>

          <CpdDropdown
            label={t('payments.cancelPaymentDialog.linkedBankAccount')}
            value={hasBankAccount ? payment.client_bank_account_id : ''}
            options={
              hasBankAccount
                ? [
                    {
                      id: payment.client_bank_account_id,
                      display_name: payment.client_bank_account_name,
                      bank_name: payment.client_bank_account_bank_name,
                      account_last_four: payment.client_bank_account_last_four,
                      business_payee_name:
                        payment.client_bank_account_business_payee_name,
                    },
                  ]
                : []
            }
            getOptionLabel={formatBankAccountDisplay}
            getOptionValue={(account) => account.id}
            disabled
            error={!hasBankAccount}
            helperText={
              hasBankAccount
                ? t('payments.cancelPaymentDialog.bankAccountInfo')
                : t('payments.cancelPaymentDialog.noBankAccountLinked')
            }
            required
          />

          <CpdTextField
            label={t('payments.cancelPaymentDialog.commentLabel')}
            placeholder={t('payments.cancelPaymentDialog.commentPlaceholder')}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            required
            multiline
            rows={3}
            error={!!fieldErrors.comment}
            helperText={fieldErrors.comment}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 2 }}>
        <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
          <Box sx={{ flex: 1 }} />

          <Button
            onClick={onClose}
            startIcon={<Cancel />}
            disabled={submitting}>
            {t('payments.cancelPaymentDialog.cancel')}
          </Button>

          <CpdActionButton
            onClick={handleConfirm}
            loading={submitting}
            disabled={isConfirmDisabled}
            startIcon={<CheckCircle sx={{ color: 'white' }} />}>
            {submitting
              ? t('common.actions.submitting')
              : t('payments.cancelPaymentDialog.confirm')}
          </CpdActionButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CancelPaymentDialog;
